import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatasetService } from '../../../../shared/dataset/service/dataset.service';
import { StudyEditorContext } from '../../../study-editor.context';
import { VariableService } from '../../../../shared/ontology/service/variable.service';
import { AlertService } from '../../../../shared/alert/alert.service';
import { StudyEditorService } from '../../../study-editor.service';
import { StudyInstanceModel } from '../../../../shared/dataset/model/study-instance.model';
import { ModalConfirmComponent } from '../../../../shared/modal/modal-confirm.component';
import { HttpResponse } from '@angular/common/http';

@Component({
    selector: 'jhi-sub-observation-unit-dataset-builder-modal',
    templateUrl: './update-sub-observation-unit-number-modal.component.html',
    styleUrl: './update-sub-observation-unit-number-modal.component.scss'
})
export class UpdateSubObservationUnitNumberModalComponent implements OnInit {

    @Input() selectedDatasetType: any;
    @Input() selectedVariableId: number;
    @Input() datasetName: string;
    @Input() datasetId: number;

    studyInstanceItems: StudyInstanceItem[] = [];
    selectAll: boolean = false;
    searchQuery: any;
    selectedVariable: any;
    numberOfSubObservationUnits = 1;
    submitted = false;
    variables: any[] = [];
    isEmptySelection = false;
    plotDatasetId: number;
    maxSubObservationParentUnit: number;
    subObservationUnitsNumberOfUnitsMap: Map<number, number>;

    constructor(private activeModal: NgbActiveModal,
                private datasetService: DatasetService,
                private studyEditorContext: StudyEditorContext,
                private variableService: VariableService,
                private alertService: AlertService,
                private studyEditorService: StudyEditorService,
                private translateService: TranslateService,
                private modalService: NgbModal) {
    }

    ngOnInit(): void {
        this.initDatasetBuilder();
    }

    cancel(): void {
        this.activeModal.dismiss();
    }

    getHeader(label: string) {
        const param = this.translateService.instant(label);
        return this.translateService.instant('study-editor.subobservation-modal.edit.header', { param });
    }

    initDatasetBuilder () {
        this.maxSubObservationParentUnit = this.studyEditorContext.maxSubObservationParentUnit;
        this.numberOfSubObservationUnits = undefined;
        this.selectedVariable = undefined;
        this.datasetService.getDatasetInstances(this.studyEditorContext.studyId, this.studyEditorContext.getPlotDataset().datasetId).toPromise().then((datasetInstances) => {
            this.studyInstanceItems = datasetInstances as StudyInstanceItem[];
            this.datasetService.getSubObservationUnitsNumberOfUnitsMap(this.studyEditorContext.studyId, this.datasetId).toPromise().then(map => {
                this.subObservationUnitsNumberOfUnitsMap = new Map(Object.entries(map).map(([k, v]) => [Number(k), v as number]));
                this.studyInstanceItems.forEach((instance) => {
                    instance.isChecked = this.subObservationUnitsNumberOfUnitsMap.has(instance.instanceId);
                    instance.numberOfSubObservationUnits = this.subObservationUnitsNumberOfUnitsMap.get(instance.instanceId);
                });
                this.selectAll = this.studyInstanceItems.length === this.subObservationUnitsNumberOfUnitsMap.size;
            });
            this.variableService.searchVariables({
                variableIds: [this.selectedVariableId]
            }).toPromise().then((variablesFiltered)=> {
                this.variables = variablesFiltered.body;
                this.variables.forEach((variable)=> {
                    this.selectedVariable = this.variables.find(variable => parseInt(variable.id, 10) === this.selectedVariableId);
                });
            });
        });

        this.plotDatasetId = this.studyEditorContext.getPlotDataset().datasetId;
    }

    doSelectAll() {
        this.studyInstanceItems.forEach((instance) => {
            instance.isChecked = this.selectAll;
        });
    }

    instanceSelected() {
        this.selectAll =  this.studyInstanceItems.every(instance => instance.isChecked);
    }

    isSaveDisabled() {
        const checkedInstances = this.studyInstanceItems.filter(instance => instance.isChecked);
        if (checkedInstances.length === 0) {
            return true;
        }
        return checkedInstances.some(instance =>
            !instance.numberOfSubObservationUnits
        );
    }

    isNumberOfSubObsUnitDecreased(instanceModel: StudyInstanceItem) {
        return this.subObservationUnitsNumberOfUnitsMap.has(instanceModel.instanceId)
            && this.subObservationUnitsNumberOfUnitsMap.get(instanceModel.instanceId) > instanceModel.numberOfSubObservationUnits;
    }

    submit() {
        if (this.studyInstanceItems.some(instance => instance.isChecked && this.isNumberOfSubObsUnitDecreased(instance))) {
            const decreasedEnvs = this.studyInstanceItems.filter(instance => instance.isChecked && this.isNumberOfSubObsUnitDecreased(instance));
            const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
            confirmModalRef.componentInstance.message =
                this.translateService.instant('study-editor.subobservation-modal.edit.confirm.warning',
                    { datasetType: this.translateService.instant(this.selectedDatasetType.label), environmentNumbers: decreasedEnvs.map(i => i.instanceNumber).join(', ') });
            confirmModalRef.result.then(() => {
                this.updateSubObservationUnitsNumberOfUnits();
            });
        } else {
            this.updateSubObservationUnitsNumberOfUnits();
        }
    };

    updateSubObservationUnitsNumberOfUnits() {
        const selectedItems = this.studyInstanceItems
            .filter(item => item.isChecked)
            .reduce((obj, item) => {
                obj[item.instanceId] = item.numberOfSubObservationUnits;
                return obj;
            }, {});
        this.datasetService.updateSubObservationUnitsNumberOfUnits(this.studyEditorContext.studyId, this.datasetId, selectedItems).toPromise().then(()=> {
            this.alertService.success('study-editor.sub-observation-dataset-builder.update.success');
            this.studyEditorService.triggerRefreshAndWait().then(() => {
                this.studyEditorService.navigateToDatasetTab(this.studyEditorContext.studyId, this.datasetId);
                this.activeModal.close();
            });
        });

    }
}

export class StudyInstanceItem extends StudyInstanceModel {
    isChecked: boolean;
    numberOfSubObservationUnits: number;
}
